import React from 'react';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import AutoAwesomeMotionOutlined from '@mui/icons-material/AutoAwesomeMotionOutlined';
import SaveOutlined from '@mui/icons-material/SaveOutlined';
import { monthLabel } from '../utils/dateHelpers';
import { buildMonthlySummaryInsights } from '../utils/monthlySummaryInsights';
import { Modal, Button, Input, Alert, DataTable, StatTile } from '../../../components/ui';

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('en-GB');
};

/**
 * Every chart here is a single-series breakdown, so every bar uses the same
 * accent. The previous version gave each chart its own hue (blue / rose /
 * emerald / amber), which implied a relationship between charts that doesn't
 * exist — colour has to earn its place by encoding something.
 */
const BarChart = ({ title, rows, emptyLabel = 'No data' }) => {
  const maxCount = rows[0]?.count || 0;

  return (
    <section className="rounded-lg border border-border bg-surface p-4">
      <h3 className="mb-3 text-sm font-semibold text-foreground">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-fg">{emptyLabel}</p>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <div key={row.label}>
              <div className="mb-1 flex items-center justify-between gap-3 text-sm">
                <span className="truncate text-foreground">{row.label}</span>
                <span className="tabular shrink-0 text-muted-fg">{row.count}</span>
              </div>
              <div
                className="h-2 overflow-hidden rounded-full bg-muted"
                role="img"
                aria-label={`${row.label}: ${row.count}`}
              >
                <div
                  className="h-full rounded-full bg-accent transition-all duration-slow ease-out"
                  style={{ width: `${maxCount ? (row.count / maxCount) * 100 : 0}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const InsightCard = ({ label, value, note }) => (
  <div className="rounded-lg border border-border bg-surface p-4">
    <p className="text-xs uppercase tracking-wide text-muted-fg">{label}</p>
    <p className="mt-1 truncate text-xl font-semibold text-foreground">{value}</p>
    {note ? <p className="mt-1.5 text-sm text-muted-fg">{note}</p> : null}
  </div>
);

const MonthlySummaryModal = ({
  isOpen,
  onClose,
  selectedMonth,
  onMonthChange,
  monthlySummary,
  monthlySummaryError,
  monthlySummaryLoading,
  generatedReports,
  generatedReportsLoading,
  onRefresh,
  onGenerate,
  userRole,
  onExportPdf,
  onGeneratePMs,
  pmGenerating,
  pmGenerationResult,
}) => {
  if (!isOpen) return null;

  const insights = buildMonthlySummaryInsights(monthlySummary);

  const generatedColumns = [
    {
      key: 'generatedAt',
      header: 'Generated at',
      primary: true,
      render: (r) => (r.generatedAt ? new Date(r.generatedAt).toLocaleString('en-GB') : '—'),
    },
    { key: 'generatedBy', header: 'Generated by', render: (r) => r.generatedBy?.username || 'system' },
    { key: 'statusFilter', header: 'Status filter', render: (r) => r.statusFilter || 'Closed' },
  ];

  const preventiveColumns = [
    { key: 'shopName', header: 'Shop', primary: true },
    {
      key: 'shopCategory',
      header: 'Category',
      render: (r) => r.shopCategory || r.shopLabel || '—',
    },
    { key: 'scNumber', header: 'SC number', className: 'tabular' },
    {
      key: 'location',
      header: 'Location',
      render: (r) => `${r.location}${r.zone ? ` / ${r.zone}` : ''}`,
    },
    { key: 'ppmType', header: 'Type' },
    {
      key: 'completedAt',
      header: 'Service check time',
      render: (r) => formatDateTime(r.completedAt),
    },
    {
      key: 'completedByUser',
      header: 'Checked by',
      render: (r) => r.completedByUser || '—',
    },
  ];

  const srColumns = [
    {
      key: 'closedAt',
      header: 'Closed time',
      primary: true,
      render: (r) => formatDateTime(r.closedAt || r.actualResolution),
    },
    {
      key: 'shopName',
      header: 'Shop',
      render: (r) => r.shopName || r.assetTag || r.location || '—',
    },
    {
      key: 'shopCategory',
      header: 'Category',
      render: (r) => r.shopCategory || r.shopLabel || '—',
    },
    {
      key: 'closedBy',
      header: 'Closed by',
      render: (r) => r.closedBy || '—',
    },
    { key: 'description', header: 'Request / work description' },
    { key: 'scNumber', header: 'SC number', className: 'tabular' },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title="Monthly report summary"
      subtitle="Closed preventive and service request records created in the selected month"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="secondary"
            icon={PictureAsPdfOutlined}
            onClick={onExportPdf}
            disabled={!monthlySummary}
          >
            Export PDF
          </Button>
        </>
      }
    >
      <div className="mb-5 flex flex-wrap items-end gap-3 border-b border-border pb-5">
        <Input
          label="Month"
          type="month"
          value={selectedMonth}
          onChange={(e) => onMonthChange(e.target.value)}
          className="w-44"
        />
        <Button
          variant="secondary"
          icon={RefreshOutlined}
          onClick={onRefresh}
          loading={monthlySummaryLoading}
        >
          Refresh
        </Button>
        {userRole === 'admin' && (
          <>
            <Button
              icon={SaveOutlined}
              onClick={onGenerate}
              loading={monthlySummaryLoading}
              title="Generate summary for selected month"
            >
              Generate &amp; save
            </Button>
            <Button
              variant="secondary"
              icon={AutoAwesomeMotionOutlined}
              onClick={onGeneratePMs}
              loading={pmGenerating}
              title="Auto-create Monthly PM work orders for all zones for the selected month"
            >
              Generate monthly PMs
            </Button>
          </>
        )}
      </div>

      {pmGenerationResult && (
        <Alert variant={pmGenerationResult.success ? 'success' : 'error'} className="mb-4">
          {pmGenerationResult.success
            ? `Monthly PMs for ${String(pmGenerationResult.month).padStart(2, '0')}/${pmGenerationResult.year}: ${pmGenerationResult.createdCount} created, ${pmGenerationResult.skippedCount} skipped (already existed).`
            : pmGenerationResult.message}
        </Alert>
      )}

      {monthlySummaryError && (
        <Alert variant="error" className="mb-4">
          {monthlySummaryError}
        </Alert>
      )}

      {monthlySummary && insights && (
        <div className="space-y-6">
          <p className="text-sm text-muted-fg">{insights.scopeNote}</p>
          <div className="grid gap-3 sm:grid-cols-3">
            <StatTile label="Total closed" value={insights.totalClosed} meta="All closed QDF records" />
            <StatTile label="Preventive" value={insights.preventiveCount} meta="Closed planned maintenance" />
            <StatTile label="Service requests" value={insights.serviceRequestCount} meta="Closed corrective work" />
          </div>

          <section className="rounded-lg border border-border bg-muted/50 p-5">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Executive summary</h3>
            <div className="space-y-3 text-sm leading-relaxed text-muted-fg">
              {insights.narrative.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </section>

          <section>
            <h3 className="mb-3 text-sm font-semibold text-foreground">Service delivery and music management</h3>
            <DataTable
              columns={[
                { key: 'label', header: 'Measure', primary: true },
                { key: 'value', header: 'Result' },
                { key: 'note', header: 'Details' },
              ]}
              rows={insights.serviceDeliveryRows}
              keyField="label"
            />
          </section>

          <section>
            <h3 className="mb-3 text-sm font-semibold text-foreground">
              Generated reports ({monthLabel(monthlySummary.year, monthlySummary.month)})
            </h3>
            <DataTable
              columns={generatedColumns}
              rows={generatedReports}
              loading={generatedReportsLoading}
              emptyTitle="No generated report"
              emptyDescription="Nothing has been saved for this month yet."
            />
          </section>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <InsightCard
              label="Busiest location"
              value={insights.busiestLocation?.label || '—'}
              note={
                insights.busiestLocation
                  ? `${insights.busiestLocation.count} closed items`
                  : 'No closed activity this month.'
              }
            />
            <InsightCard
              label="Top priority"
              value={insights.dominantPriority?.label || '—'}
              note={
                insights.dominantPriority
                  ? `${insights.dominantPriority.count} records`
                  : 'No priority trend recorded.'
              }
            />
            <InsightCard
              label="Top PPM type"
              value={insights.topPpmType?.label || '—'}
              note={
                insights.topPpmType
                  ? `${insights.topPpmType.count} preventive closures`
                  : 'No preventive work closed.'
              }
            />
            <InsightCard
              label="Top SR theme"
              value={insights.topIssue?.count || 0}
              note={insights.topIssue ? insights.topIssue.description : 'No service request records this month.'}
            />
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <BarChart title="Closed workload by location" rows={insights.locationRows} />
            <BarChart title="Priority mix" rows={insights.priorityRows} />
            <BarChart
              title="Preventive program mix"
              rows={insights.ppmRows}
              emptyLabel="No preventive closures"
            />
            <BarChart
              title="Top service request themes"
              rows={insights.srRows.map((row) => ({ label: row.description, count: row.count }))}
              emptyLabel="No service request themes"
            />
          </div>

          <section>
            <h3 className="mb-3 text-sm font-semibold text-foreground">Preventive inspection results</h3>
            <DataTable
              columns={[
                { key: 'description', header: 'Check performed', primary: true },
                { key: 'Pass', header: 'Pass' },
                { key: 'Fail', header: 'Fail' },
                { key: 'N/A', header: 'N/A' },
                { key: 'Unrecorded', header: 'No result' },
              ]}
              rows={insights.evidence.checklistRows}
              keyField="description"
              emptyTitle="No inspection checklist data recorded"
            />
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold text-foreground">Recorded failed checks</h3>
            <p className="mb-3 text-sm text-muted-fg">Inspection findings and technician remarks; follow-up status is not tracked here.</p>
            <DataTable
              columns={[
                { key: 'scNumber', header: 'SC number', primary: true },
                { key: 'shopName', header: 'Shop / zone' },
                { key: 'description', header: 'Failed check' },
                { key: 'remarks', header: 'Remarks' },
              ]}
              rows={insights.evidence.findings}
              emptyTitle="No failed checks recorded"
            />
          </section>

          <section>
            <h3 className="mb-3 text-sm font-semibold text-foreground">Spare parts recorded during PM</h3>
            <DataTable
              columns={[
                { key: 'scNumber', header: 'SC number', primary: true },
                { key: 'shopName', header: 'Shop / zone' },
                { key: 'deviceName', header: 'Device' },
                { key: 'description', header: 'Part / description' },
                { key: 'quantity', header: 'Quantity' },
                { key: 'remarks', header: 'Remarks' },
              ]}
              rows={insights.evidence.spareParts}
              emptyTitle="No spare parts recorded"
            />
          </section>

          {insights.evidence.comments.length > 0 && (
            <section>
              <h3 className="mb-3 text-sm font-semibold text-foreground">Technician observations</h3>
              <DataTable
                columns={[
                  { key: 'scNumber', header: 'SC number', primary: true },
                  { key: 'shopName', header: 'Shop / zone' },
                  { key: 'comments', header: 'Observation' },
                ]}
                rows={insights.evidence.comments}
              />
            </section>
          )}

          <section className="rounded-lg border border-border bg-surface p-4">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Closed work orders by creation day (UTC)</h3>
            {insights.timelineRows.length === 0 ? (
              <p className="text-sm text-muted-fg">No closed activity in the selected month.</p>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-6">
                {insights.timelineRows.map((row) => (
                  <div key={row.label} className="rounded-md border border-border bg-muted/50 p-3">
                    <p className="text-xs uppercase tracking-wide text-muted-fg">Day {row.label}</p>
                    <p className="tabular mt-1 text-xl font-semibold text-foreground">{row.total}</p>
                    <p className="mt-1 text-xs text-muted-fg">
                      PPM {row.preventive} · SR {row.serviceRequests}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>

          <div className="grid gap-6 xl:grid-cols-2">
            <section>
              <h3 className="mb-3 text-sm font-semibold text-foreground">
                PM service checks ({insights.preventiveShopRows.length})
              </h3>
              <DataTable
                columns={preventiveColumns}
                rows={insights.preventiveShopRows}
                keyField="id"
                emptyTitle="No preventive records"
                emptyDescription="No closed PM service checks were recorded for this month."
              />
            </section>

            <section>
              <h3 className="mb-3 text-sm font-semibold text-foreground">
                Service request summary ({insights.serviceRequestSummaryRows.length})
              </h3>
              <DataTable
                columns={srColumns}
                rows={insights.serviceRequestSummaryRows}
                keyField="id"
                emptyTitle="No service request records"
                emptyDescription="No closed service requests were recorded for this month."
              />
            </section>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default MonthlySummaryModal;
